import { useId, useState } from 'react';
import { formatRevenue } from '../../utils/revenueFormat';
import { monthlyAvailableHours } from '../../utils/availabilitySchedule';

// Host receives 70% of gross revenue; platform retains 30%.
export const HOST_TAKE_RATE = 0.7;

/**
 * Monthly revenue estimate:
 *   spots × occupancy% × hours-of-availability-per-month × rate × host-take
 * Any missing input returns null so the caller can show a placeholder.
 */
export function computeMonthlyRevenue(spots, rate, occupancyPct, hoursPerMonth) {
    if (spots == null || rate == null || hoursPerMonth == null) return null;
    const revenue = Number(spots) * (Number(occupancyPct) / 100)
        * Number(hoursPerMonth) * Number(rate) * HOST_TAKE_RATE;
    if (!Number.isFinite(revenue)) return null;
    return Math.round(revenue);
}

export default function RevenueEstimateCard({
    spots,
    hourlyRate,
    availability,
    defaultOccupancy = 50,
}) {
    const id = useId();
    const [occupancy, setOccupancy] = useState(defaultOccupancy);

    const hoursPerMonth = monthlyAvailableHours(availability);
    const isMissing = spots == null || spots === '' || hourlyRate == null || hourlyRate === '' || hoursPerMonth <= 0;
    const spotsNum = Number(spots);
    const rateNum = Number(hourlyRate);
    const bookedSpots = !isMissing ? Math.round(spotsNum * (occupancy / 100)) : null;
    const monthly = !isMissing
        ? computeMonthlyRevenue(spotsNum, rateNum, occupancy, hoursPerMonth)
        : null;

    return (
        <div className="host-summary-revenue">
            <div className="host-summary-revenue-label">Estimated monthly revenue</div>
            {isMissing ? (
                <div className="host-summary-revenue-missing">
                    Add an hourly rate, spot count, and availability to see your estimate.
                </div>
            ) : (
                <>
                    <div className="host-summary-revenue-value">
                        {formatRevenue(monthly)}
                        /mo
                    </div>
                    <div className="host-summary-revenue-sub">
                        <span className="host-summary-revenue-source">From your input</span>
                        {bookedSpots}
                        {' '}
                        of
                        {' '}
                        {spotsNum}
                        {' '}
                        spots booked · $
                        {rateNum.toFixed(2)}
                        /hr · after 30% platform fee
                    </div>
                    <div className="host-summary-revenue-slider-row">
                        <label htmlFor={id}>Utilization</label>
                        <input
                            id={id}
                            className="host-summary-revenue-slider"
                            type="range"
                            min={10}
                            max={100}
                            step={5}
                            value={occupancy}
                            onChange={(e) => setOccupancy(Number(e.target.value))}
                        />
                        <div className="host-summary-revenue-utilval">
                            {occupancy}
                            %
                        </div>
                    </div>
                    <div className="host-summary-revenue-disclaimer">
                        Revenue is an estimate. Excludes applicable taxes and fees.
                    </div>
                </>
            )}
        </div>
    );
}
